<template>
    <template v-if="!props.isEmbedded">
        <button
            type="button"
            @click="toggleModal"
            class="search-button"
        >
            <font-awesome-icon
                :icon="['far', 'search']"
                class="search-button-icon"
            />
            <div class="search-button-hidden-text">{{ isMobileView ? 'Search the site' : 'ctrl + k' }}</div>
        </button>

        <central-modal
            header="Search"
            v-model:visible="isModalVisible"
            :size="{ width: '50vw', height: '70vh' }"
        >
            <search-bar
                :is-embedded="false"
                @close="isModalVisible = false"
                @select="(resultTitle: string, resultId: string) => selectResult(resultTitle, resultId)"
            />
        </central-modal>
    </template>
    <template v-else>
        <transition>
            <div class="inline-mask" v-if="inlineFocused" />
        </transition>
        <div class="search-input">
            <search-bar
                is-embedded
                :search-string-initial="props.searchString"
                :scroll-container="props.scrollContainer"
                v-model:is-focused="inlineFocused"
                @select="(resultTitle: string, resultId: string) => selectResult(resultTitle, resultId)"
                @update:searchStringInitial="updateSearchStringProp"
                :class="{ 'high-z-index': inlineFocused }"
            />
        </div>
    </template>
    <central-modal
        :header="companyOpenInModal ?? ''"
        v-model:visible="companyVisible"
        v-if="companyOpenInModal"
        hide-header
        size="medium"
    >
        <company-panel
            :selected-company="companyOpenInModal"
            :lookAtAllDataProp="true"
        />
    </central-modal>
    <central-modal
        :header="projectOpenInModal?.projectName ?? ''"
        v-model:visible="projectVisible"
        v-if="projectOpenInModal"
        hide-header
        size="medium"
    >
        <project-panel
            :selectedProject="projectOpenInModal"
        />
    </central-modal>

    <plant-switch
        v-if="plantOpenInModal"
        v-model:visible="plantVisible"
        :plant-id="plantOpenInModal"
    />
    <definition-modal
        v-if="definitionOpenInModal"
        :definition="definitionOpenInModal"
        @close:modal="definitionOpenInModal = null"
    />
</template>

<script lang="ts" setup>
import {
    onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { IDefinition } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';
import CentralModal from '@/components/common/CentralModal.vue';
import CompanyPanel from '@/components/storageTracking/companySearch/CompanyPanel.vue';
import ProjectPanel from '@/components/storageTracking/projectSearch/ProjectPanel.vue';
import SearchBar from '@/components/powerMarkets/input/SearchBar.vue';
import DefinitionModal from '@/components/powerMarkets/modals/DefinitionModal.vue';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { useDefinitionStore } from '@/stores/powerMarkets/definitionStore.ts';
import { storeToRefs } from 'pinia';
import PlantSwitch from '../plant/PlantSwitch.vue';

const props = defineProps<{
    isEmbedded: boolean;
    searchString?: string;
    scrollContainer?: HTMLElement | null;
}>();

const emit = defineEmits<{
    'update:searchString': [searchString: string];
}>();

const router = useRouter();
const definitionStore = useDefinitionStore();

const uiStore = useUIStore();
const { isMobileView } = storeToRefs(uiStore);

const isModalVisible = ref<boolean>(false);
const projectVisible = ref<boolean>(false);
const companyVisible = ref<boolean>(false);
const plantVisible = ref<boolean>(false);
const companyOpenInModal = ref<string | null>(null);
const projectOpenInModal = ref<ICsvData | null>(null);
const plantOpenInModal = ref<string | null>(null);
const definitionOpenInModal = ref<IDefinition | null | undefined>(null);
const inlineFocused = ref<boolean>(false);

const modalResultTitles = new Set(['Glossary', 'Storage Assets', 'Storage Companies']);

function updateSearchStringProp (value: string): void {
    emit('update:searchString', value);
}

function openPlantModal (plantId: string): void {
    plantOpenInModal.value = plantId;
    plantVisible.value = true;
}

async function openDefinitionModal (definitionId: string): Promise<void> {
    definitionOpenInModal.value = await definitionStore.getDefinitionById(definitionId);
}

function opensDetailModal (resultTitle: string): boolean {
    return resultTitle.includes('Plant') || modalResultTitles.has(resultTitle);
}

async function selectResult (resultTitle: string, resultId: string): Promise<void> {
    if (props.isEmbedded) {
        inlineFocused.value = false;
    }

    if (!props.isEmbedded && !opensDetailModal(resultTitle)) {
        isModalVisible.value = false;
    }

    if (resultTitle.includes('Plant')) {
        openPlantModal(resultId);
    } else {
        switch (resultTitle) {
            case 'Glossary':
                await openDefinitionModal(resultId);
                break;
            case 'Pages':
                if (resultId.includes('/')) {
                    await router.push(resultId);
                } else {
                    await router.push({ name: resultId });
                }
                break;
            case 'Storage Assets': {
                const asFactStorageId = parseInt(resultId, 10);
                DataApi.getProjectById(asFactStorageId).then((project) => {
                    projectOpenInModal.value = project;
                    projectVisible.value = true;
                });
                break;
            }
            case 'Storage Companies':
                companyOpenInModal.value = resultId;
                companyVisible.value = true;
                break;
            case 'Research':
                await router.push({ name: deltaGptRoutes.documentView.name, params: { documentShortCode: resultId } });
                break;
        }
    }
}

function toggleModal (): void {
    isModalVisible.value = !isModalVisible.value;
}

onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown);
});

function handleKeydown (e: KeyboardEvent): void {
    if (e.ctrlKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggleModal();
    }
}

</script>

<style lang="scss" scoped>
.search-input {
    width: 35vw;
    position: relative;

    @include responsive(sm) {
        width: 100%;
    }

    & .high-z-index {
        z-index: 111;
    }
}

.v-enter-active,
.v-leave-active {
    transition: opacity 0.4s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

.inline-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 110;
}

.search-button {
    position: relative;
    border-radius: 20px;
    width: 40px;
    height: 40px;
    display: flex;
    transition: ease 0.5s;
    overflow: hidden;
    white-space: nowrap;

    @include responsive(sm) {
        width: revert;
        flex-direction: row-reverse;
        position: unset;

        &-hidden-text {
            display: block;
        }
    }

    @include responsive(mdUp) {
        &:hover {
            background-color: var(--neutral200);
            font-size: 2.2rem;
            width: 97px;

            .search-button-hidden-text {
                display: block;
            }
        }
    }

    &-icon {
        margin: auto;
        transition: ease 0.5s;
    }

    &-hidden-text {
        display: none;
        font-size: var(--smallFontSize);
        margin: auto;
        padding: 0 var(--defaultSpacing);
        color: var(--neutral500);

        @include responsive(sm) {
            display: block;
        }
    }
}

</style>
